"use client";

import React from "react";
import { Box, Container, Typography, CircularProgress, Fade, Grow, keyframes, useTheme, alpha, Paper, LinearProgress, styled } from "@mui/material";
import Logo from "@/Images/jobstacky_light_icon_no_bg.png";
import DarkLogo from "@/Images/jobstacky_dark_icon_no_bg.png";
import Image from "next/image";

// Custom animations
const pulse = keyframes`
  0% {
	opacity: 0.6;
  }
  50% {
	opacity: 1;
  }
  100% {
	opacity: 0.6;
  }
`;

const slideUp = keyframes`
  from {
	opacity: 0;
	transform: translateY(20px);
  }
  to {
	opacity: 1;
	transform: translateY(0);
  }
`;

// Styled components
const LoadingContainer = styled(Box)(({ theme }) => ({
	minHeight: "100vh",
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	background: `linear-gradient(135deg, ${theme.palette.mode === "dark" ? "#0a0a0a" : "#fafafa"} 0%, ${theme.palette.mode === "dark" ? "#1a1a1a" : "#f0f0f0"
		} 100%)`,
	position: "relative",
	overflow: "hidden",
}));

const BrandContainer = styled(Box)({
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	marginBottom: "2rem",
});

const LoadingContent = styled(Box)({
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	gap: "1.5rem",
});

const CustomCircularProgress = styled(CircularProgress)(({ theme }) => ({
	"& .MuiCircularProgress-circle": {
		strokeLinecap: "round",
	},
}));

const LoadingText = styled(Typography)(({ theme }) => ({
	animation: `${pulse} 2s ease-in-out infinite`,
	color: theme.palette.text.secondary,
	fontWeight: 300,
	letterSpacing: "0.5px",
}));

const ProgressContainer = styled(Box)({
	width: "200px",
	marginTop: "1rem",
});

// Background decoration
const BackgroundOrb = styled(Box)(({ theme }) => ({
	position: "absolute",
	borderRadius: "50%",
	background: `radial-gradient(circle, ${theme.palette.primary.main}20, transparent 70%)`,
	animation: `${pulse} 4s ease-in-out infinite`,
	pointerEvents: "none",
}));

export default function LoadingPage() {
	const theme = useTheme();
	const [progress, setProgress] = React.useState(0);

	React.useEffect(() => {
		const timer = setInterval(() => {
			setProgress((prevProgress) => {
				const newProgress = prevProgress + Math.random() * 15;
				return newProgress >= 100 ? 0 : newProgress;
			});
		}, 500);

		return () => {
			clearInterval(timer);
		};
	}, []);

	return (
		<LoadingContainer>
			{/* Background decorative elements */}
			<BackgroundOrb
				sx={{
					width: 300,
					height: 300,
					top: "10%",
					right: "10%",
					animationDelay: "0s",
				}}
			/>
			<BackgroundOrb
				sx={{
					width: 200,
					height: 200,
					bottom: "20%",
					left: "15%",
					animationDelay: "2s",
				}}
			/>

			<Fade in>
				<Box>
					{/* Brand Section */}
					<BrandContainer>
						{/* Logo */}
						<Image
							src={theme.palette.mode === "dark" ? DarkLogo.src : Logo.src}
							width={200}
							height={100}
							alt="Jobstacky Logo"
							style={{ maxWidth: 200, maxHeight: 200, width: "200px", height: "100px", objectFit: "contain" }}
						/>

						<Typography
							variant="h5"
							sx={{
								mt: 1,
								fontWeight: 600,
								background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
								backgroundClip: "text",
								WebkitBackgroundClip: "text",
								WebkitTextFillColor: "transparent",
								textAlign: "center",
							}}
						>
							Jobstacky
						</Typography>
					</BrandContainer>

					{/* Loading Section */}
					<LoadingContent>
						<CustomCircularProgress
							size={48}
							thickness={3}
							sx={{
								color: theme.palette.mode === "dark" ? theme.palette.primary.light : theme.palette.primary.main,
							}}
						/>

						<LoadingText variant="body1">Loading your experience...</LoadingText>

						<ProgressContainer>
							<LinearProgress
								variant="determinate"
								value={progress}
								sx={{
									height: 3,
									borderRadius: 2,
									backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
									"& .MuiLinearProgress-bar": {
										borderRadius: 2,
										background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
									},
								}}
							/>
						</ProgressContainer>
					</LoadingContent>
				</Box>
			</Fade>
		</LoadingContainer>
	);
}
